import {
  Injectable,
  LOCALE_ID,
  computed,
  inject,
  signal
} from '@angular/core';
import { Observable, tap } from 'rxjs';

import { listenWebsocketEvents } from '@core/websocket';

import { AuthState } from '../users';
import { PaymentTabs } from '../payment/payment.entity';

import {
  ApiResponse,
  PreferredWallet,
  Withdrawal,
  WithdrawalContext,
  WithdrawalMethod,
  WithdrawalTokens
} from './withdrawals.entity';
import { WithdrawalsService } from './withdrawals.service';

@Injectable({ providedIn: 'root' })
export class WithdrawalsState {
  private readonly withdrawalsService = inject(WithdrawalsService);
  private readonly authState = inject(AuthState);
  private readonly isRuLanguage = 'ru' === inject(LOCALE_ID);

  public readonly withdrawals = signal<Withdrawal[]>([]);
  public readonly methods = signal<WithdrawalMethod[]>([]);
  public readonly cryptoTokens = signal<WithdrawalTokens>({});
  public readonly preferredWallets = signal<PreferredWallet[]>([]);
  public readonly currentWalletId = signal<number|null>(null);
  public readonly currentMethodId = signal<number|null>(null);
  public readonly currentCryptoMethodId = signal<number|null>(null);
  public readonly currentCryptoToken = signal<string|null>(null);

  public readonly cryptoTokensTitle = signal<string[]>([]);

  public readonly currentMethod = computed(() => {
    const currentMethodId = this.currentMethodId();

    if(null === currentMethodId) {
      return undefined;
    }

    return this.methods().find((method) => currentMethodId === method.id);
  });

  public readonly currentCryptoMethod = computed(() => {
    const currentCryptoMethodId = this.currentCryptoMethodId();
    const token = this.currentCryptoToken();

    if(null === currentCryptoMethodId || null === token) {
      return undefined;
    }

    return this.cryptoTokens()[token].networks.find((method) => currentCryptoMethodId === method.id);
  });

  public readonly currentMethodWallets = computed(() => {
    const currentMethodId = this.currentMethodId();
    const wallets = this.preferredWallets();

    return wallets.filter((wallet) => wallet.method.id === currentMethodId);
  });

  public readonly currentWallet = computed(() => {
    const currentWalletId = this.currentWalletId();

    if(null === currentWalletId) {
      return undefined;
    }

    const currentMethodId = this.currentMethodId();
    const wallet = this.preferredWallets().find((wallet) => currentWalletId === wallet.id);

    if(wallet && wallet.method.id !== currentMethodId) {
      return undefined;
    }

    return wallet;
  });

  private readonly wsEvents = listenWebsocketEvents({
    'withdrawals.withdrawal_updated': (data) => this.onWsWithdrawalUpdated(data)
  });

  private readonly REFERRAL_METHOD: WithdrawalMethod = {
    id: 1,
    name: $localize`:@@payment.tabs.ref:Реф. Баланс`,
    key: PaymentTabs.REFERRAL,
    provider: 'referral',
    minAmountOnAccountToShow: 0,
    icon: null,
    minAmount: 0,
    maxAmount: 0,
    feePct: '0'
  };

  private readonly HORIZON_METHOD: WithdrawalMethod = {
    id: 1,
    name: $localize`:@@payment.tabs.cards:Карты`,
    key: PaymentTabs.HORIZON,
    provider: 'card_withdrawal',
    minAmountOnAccountToShow: 0,
    icon: null,
    minAmount: 0,
    maxAmount: 0,
    feePct: '0'
  };

  setCurrentWalletId(walletId: number): void {
    this.currentWalletId.set(walletId);
  }

  setCurrentMethodId(methodId: number): void {
    this.currentMethodId.set(methodId);
  }

  setCurrentCryptoMethodId(methodId: number): void {
    this.currentCryptoMethodId.set(methodId);
  }

  loadWithdrawals(): Observable<ApiResponse.Withdrawals> {
    return this.withdrawalsService.getWithdrawals().pipe(
      tap((response: ApiResponse.Withdrawals) => {
        this.authState.patchUser('needPlayGames', response.needPlayGames);
        this.withdrawals.set(response.withdrawals);
      })
    );
  }

  loadTokens(): Observable<ApiResponse.Tokens> {
    return this.withdrawalsService.getTokens().pipe(
      tap((response: ApiResponse.Tokens) => {
        this.cryptoTokens.set(response.tokens);
        this.cryptoTokensTitle.set(Object.keys(response.tokens));
      })
    );
  }

  loadMethods(): Observable<ApiResponse.Methods> {
    return this.withdrawalsService.getMethods().pipe(
      tap((response: ApiResponse.Methods) => {
        let transformedMethods = [...response.methods];
        const horizonIndex = transformedMethods.findIndex(m => m.provider === 'card_withdrawal');

        if (horizonIndex !== -1) {
          transformedMethods[horizonIndex] = {
            ...transformedMethods[horizonIndex],
            name: $localize`:@@payment.tabs.cards:Карты`,
            key: PaymentTabs.HORIZON
          };
        }

        transformedMethods.push(this.REFERRAL_METHOD);

        if (!this.isRuLanguage) {
          transformedMethods = transformedMethods.filter(
            m => m.provider !== PaymentTabs.GGPAY && m.provider !== 'card_withdrawal'
          );
        }

        this.methods.set(transformedMethods);
        this.preferredWallets.set(response.preferredWallets);
        this.currentMethodId.set(response.methods[0]?.id || null);
        this.currentWalletId.set(response.preferredWallets[0]?.id || null);
      })
    );
  }

  doWithdraw(amount: number, context: Partial<WithdrawalContext>, isRefBalance = false) {
    const currentMethodId = this.currentMethodId();

    return this.withdrawalsService.doWithdraw(
      currentMethodId!,
      amount,
      context,
      isRefBalance
    ).pipe(
      tap((response: ApiResponse.CreatedWithdrawal) => {
        const hasThisWallet = !!this.preferredWallets().find((wallet) => wallet.id === response.preferredWallet.id);

        this.withdrawals.set([response.withdrawal, ...this.withdrawals()]);

        if(!hasThisWallet) {
          this.preferredWallets.set([response.preferredWallet, ...this.preferredWallets()]);
        }
      })
    );
  }

  cancelWithdrawal(id: number): Observable<void> {
    return this.withdrawalsService.cancelWithdrawal(id);
  }

  private onWsWithdrawalUpdated(withdrawal: Withdrawal): void {
    this.withdrawals.update((withdrawals) => {
      const index = withdrawals.findIndex((row) => row.id === withdrawal.id);

      withdrawals[index] = {
        ...withdrawals[index],
        ...withdrawal
      };

      return [...withdrawals];
    });
  }
}
